<template>
  <v-autocomplete
    v-model="selected"
    :autofocus="autofocus"
    :items="items"
    :loading="loading"
    :search-input.sync="search"
    :menu-props="{ offsetY: true, maxHeight: 480, contentClass: 'global-search-menu' }"
    class="global-search"
    background-color="white"
    color="primary"
    item-text="name"
    item-value="searchKey"
    placeholder="Search parents, students, donors, applications…"
    prepend-inner-icon="mdi-magnify"
    return-object
    no-filter
    hide-details
    hide-no-data
    clearable
    outlined
    dense
    @change="viewProfile"
  >
    <template v-slot:item="{ item }">
      <v-list-item-avatar color="#F1F8F2" size="38">
        <v-icon color="primary" size="20">{{ resultIcon(item.forProfile) }}</v-icon>
      </v-list-item-avatar>
      <v-list-item-content>
        <v-list-item-title>{{ item.name }}</v-list-item-title>
        <v-list-item-subtitle>
          {{ resultTypeLabel(item.forProfile) }}<span v-if="item.phoneNumber"> · {{ item.phoneNumber }}</span>
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-icon><v-icon color="#A0AAA0" size="18">mdi-arrow-top-right</v-icon></v-list-item-icon>
    </template>
    <template v-slot:no-data>
      <v-list-item><v-list-item-title>Enter at least 3 characters</v-list-item-title></v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
import customerService from "@/modules/Customer/service.js";
import donorService from "@/modules/Donors/service.js";
import studentService from "@/modules/Student/service.js";
import applicationService from "@/modules/Application/service.js";

export default {
  name: "global-search",
  props: { autofocus: { type: Boolean, default: false } },
  data() {
    return { search: null, selected: null, items: [], loading: false, searchTimer: null };
  },
  watch: {
    search(val) {
      clearTimeout(this.searchTimer);
      if (!val || val.length < 3) {
        this.items = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.loadResults(val), 300);
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    resultIcon(type) {
      return { Customer: "mdi-account-outline", Donor: "mdi-hand-coin-outline", Student: "mdi-account-school-outline", Application: "mdi-file-account-outline" }[type] || "mdi-magnify";
    },
    resultTypeLabel(type) {
      return type === "Customer" ? "Parent" : type;
    },
    async loadResults(query) {
      this.loading = true;
      const requests = [
        customerService.getAll({ search: query }).then(res => (res.data || []).map(row => ({ ...row, forProfile: "Customer" }))),
        donorService.getAll({ search: query }).then(res => (res.data || []).map(row => ({ ...row, forProfile: "Donor" }))),
        studentService.getAll({ search: query }).then(res => (res.data || []).map(row => ({ ...row, name: row.fullName, forProfile: "Student" }))),
        applicationService.getAllStudent({ search: query }).then(res => (res.data || []).map(row => ({ ...row, name: `#${row.localId} ${row.fullName}`, forProfile: "Application" }))),
      ];
      const results = await Promise.allSettled(requests);
      if (this.search === query) {
        const groups = [
          { title: "Parents", result: results[0] },
          { title: "Donors", result: results[1] },
          { title: "Students", result: results[2] },
          { title: "Applications", result: results[3] },
        ];
        this.items = groups.reduce((items, group) => {
          const groupItems = group.result.status === "fulfilled" ? group.result.value : [];
          if (!groupItems.length) return items;
          if (items.length) items.push({ divider: true });
          items.push({ header: group.title });
          groupItems.forEach((item, index) => {
            items.push({
              ...item,
              searchKey: `${item.forProfile}-${item.id}-${index}`,
            });
          });
          return items;
        }, []);
        this.loading = false;
      }
    },
    viewProfile(item) {
      if (!item || !item.forProfile) return;
      this.$emit("searched");
      if (item.forProfile === "Customer") {
        this.$router.push({ path: `/parents/${item.id}/profile` });
        this.$events.emit("customerProfileEvent");
      } else if (item.forProfile === "Donor") {
        this.$router.push({ path: `/donors/${item.id}/profile` });
        this.$events.emit("customerProfileEvent");
      } else if (item.forProfile === "Student") {
        this.$router.push({ name: "customer-profile", params: { tab: 1, id: item.Customer.id } });
        this.$events.emit("customerProfileEvent");
      } else {
        this.$router.push({ path: `/applications/${item.Lead.id}/profile` });
        this.$events.emit("applicationProfileEvent");
      }
      this.selected = null;
      this.search = null;
    },
  },
};
</script>

<style scoped>
.global-search { width: 100%; }
.global-search ::v-deep .v-input__slot { min-height: 42px !important; border: 0 !important; border-radius: 10px !important; box-shadow: 0 1px 2px rgba(15, 23, 42, .08); }
.global-search ::v-deep .v-input__prepend-inner { margin-right: 6px; }
.global-search ::v-deep input {
  color: #1f2937 !important;
  caret-color: #2e7d32 !important;
  font-size: 14px;
}
.global-search ::v-deep input::placeholder { color: #7b8794 !important; opacity: 1; }
.global-search ::v-deep .v-select__selection { color: #1f2937 !important; }
</style>
